import moment from "moment";
import firebase from "firebase/compat/app";
import { parsePhoneNumber } from "./phoneNumbersUtils";
import { db, firebaseApp } from "firebaseConfig";
import { GenericConverter } from "utils/firestoreConverters";
import { minisitePresentationFields, pickMinisitePage } from "utils/minisite/minisitePage";
import type { ListingPresentationEdits } from "utils/minisite/minisitePage";
import { lookupListingPresentationForAdd } from "utils/minisite/listingPresentationForAdd";
import type {
  ListingsRealtorBlacklist,
  ListingsRealtorSavedList,
  FavoriteListingsOfContact
} from "../../@types/sharedSchema";
import {
  getStringListingHiddenPicturesIndex,
  getListingPicturesVisibility
} from "components/_dashboard/listingsMatch/ListingPicturesVisibilty";
import { getStringListingDescriptionOverrideIndex } from "components/_dashboard/listingsMatch/ListingMatchDescriptionOverride";

export function parseFirestoreDocument(obj: firebase.firestore.DocumentData) {
  // Converting firestore timestamp to Date
  const newObj = Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [
      k,
      v instanceof firebase.firestore.Timestamp ? v.toDate() : v
    ])
  );

  return newObj;
}

export function getUpdatedTimeFromFirestoreDoc(doc: any) {
  const seconds = doc?._delegate?._document?.version?.timestamp?.seconds;
  const dateObj = new Date(seconds * 1000);
  if (moment(dateObj)?.isBefore(moment().subtract(40, "years"))) {
    return null;
  } else {
    return dateObj;
  }
}

export function removeUndefined(data) {
  Object.keys(data).forEach((key) => data[key] === undefined && delete data[key]); //filtering out 'undefined'
}

export function generateMetadataFromContact(email: string, contactId: string) {
  const _metadata = {
    callerPhoneNumber: parsePhoneNumber(contactId),
    firestorePath: `dashboard-users-v2/${email}/contacts/${contactId}`
  };

  return _metadata;
}

export function getFavoritesListingsQuery(userEmail: string, contactId: string) {
  return db
    .collection(`favorite-listings-of-contact`)
    .where("realtorId", "==", userEmail)
    .where("contactId", "==", contactId)
    .withConverter(GenericConverter<FavoriteListingsOfContact>());
}

export function getUserFavoritesListingsQuery(userEmail: string) {
  return db
    .collection(`favorite-listings-of-contact`)
    .where("realtorId", "==", userEmail)
    .where("contactId", "==", "")
    .withConverter(GenericConverter<FavoriteListingsOfContact>());
}

// TODO - add an index for this
export function getContactsThatHaveListingInFavoritesQuery(userEmail: string, listingId: string) {
  return db
    .collection(`favorite-listings-of-contact`)
    .where("realtorId", "==", userEmail)
    .where("listingIds", "array-contains", listingId)
    .withConverter(GenericConverter<FavoriteListingsOfContact>());
}

export const createFavoriteIfNotExists = async (userEmail: string, contactId: string) => {
  try {
    const contactSnapshot = await getFavoritesListingsQuery(userEmail, contactId).get();

    if (contactSnapshot.empty) {
      console.log("No favorite listings found for this contact and user. Will create one...");

      const newFavorite: FavoriteListingsOfContact = {
        contactId: contactId,
        realtorId: userEmail,
        listingIds: [],
        organizationIds: [],
        listingsListsIds: [],
        additionalDetails: {},
        listingIdsUserLiked: [],
        listingIdsUserVisited: [],
        listingIdsUserDisliked: [],
        listingIdsWithPicturesHidden: []
      };

      const newContactSnapshot = await db
        .collection(`favorite-listings-of-contact`)
        .add(newFavorite);

      return newContactSnapshot.id;
    }

    // The page the buyer was sent, when there are several (`pickMinisitePage`).
    const favoriteContactId = (pickMinisitePage(contactSnapshot.docs) ?? contactSnapshot.docs[0]).id;

    return favoriteContactId;
  } catch (error) {
    console.error("Error creating favorite listings for contact", error);
  }
};

export const createFavoriteIfNotExistsAndAddListingToFavorites = async (
  userEmail: string,
  contactId: string,
  listingId: string
) => {
  await createFavoriteIfNotExists(userEmail, contactId);

  const favoriteDocs = await getFavoritesListingsQuery(userEmail, contactId).get();

  if (favoriteDocs.empty) {
    console.error("Got empty favorites after creation, should not happen");
    return;
  }

  const favoriteDoc = pickMinisitePage(favoriteDocs.docs) ?? favoriteDocs.docs[0];

  await addListingToFavorites(favoriteDoc.ref.path, listingId);

  return favoriteDoc.id;
};

// Awaited so a caller's try/catch actually sees a failed write (it used to resolve immediately).
export const addListingToFavorites = async (favoritePath: string, listingId: string) => {
  await addListingsToFavorites(favoritePath, [listingId]);
};

/**
 * Puts listings on a buyer's page, each NEW one carrying the realtor's presentation of it — their
 * text and the photos they hide from every buyer (`getListingPresentationForAdd`). A listing already on
 * the page is left exactly as it is.
 *
 * Every add in the dashboard comes through here or through `createFavoriteIfNotExistsAndAddListingWithEdits`
 * (the bulk message, the matches tables, the legacy listings-match page, the contact's minisite tab), as
 * the server's do (Daniela, the listing share, the campaigns). Until 2026-10-30 most of them wrote the bare
 * listing, so a photo the realtor had hidden from every buyer showed on the page it was added to, while
 * the presentation dialog promised "יחולו על כל לקוח שיקבל את הנכס".
 */
export const addListingsToFavorites = async (favoritePath: string, listingIds: string[]) => {
  console.log("About to add listings to favorites", listingIds);

  if (!favoritePath) {
    console.warn(
      "No existing favorites found for this contact and user... Should not happen, we have it set above"
    );
    return;
  }

  const page = (
    await db.doc(favoritePath).withConverter(GenericConverter<FavoriteListingsOfContact>()).get()
  ).data();
  const onPage = new Set(page?.listingIds ?? []);
  const newListingIds = Array.from(new Set(listingIds)).filter((id) => id && !onPage.has(id));
  if (!newListingIds.length) return;

  const realtor = page?.realtorId ?? firebaseApp.auth().currentUser?.email ?? "";
  const edits = await Promise.all(
    newListingIds.map((listingId) => getListingPresentationForAdd(realtor, listingId))
  );
  await writeListingsToFavorites(
    favoritePath,
    newListingIds.map((listingId, index) => ({ listingId, edits: edits[index] }))
  );
};

/**
 * Writes listings onto a page — each with the presentation given (null: shown as the ad is) — in one
 * update, the fields exactly as the server's save writes them (`minisitePresentationFields`). A second,
 * small update only when one listing hides every photo while another does not: one transform per field.
 */
const writeListingsToFavorites = async (
  favoritePath: string,
  entries: { listingId: string; edits: ListingPresentationEdits | null }[]
): Promise<void> => {
  if (!entries.length) return;
  const update: Record<string, unknown> = {
    listingIds: firebase.firestore.FieldValue.arrayUnion(...entries.map((entry) => entry.listingId))
  };
  const hideAll: string[] = [];
  const shown: string[] = [];
  for (const { listingId, edits } of entries) {
    if (!edits) continue;
    const { fields, hidesAllPictures } = minisitePresentationFields(listingId, edits);
    Object.assign(update, fields);
    (hidesAllPictures ? hideAll : shown).push(listingId);
  }
  if (hideAll.length) {
    update.listingIdsWithPicturesHidden = firebase.firestore.FieldValue.arrayUnion(...hideAll);
  } else if (shown.length) {
    update.listingIdsWithPicturesHidden = firebase.firestore.FieldValue.arrayRemove(...shown);
  }
  if (hideAll.length || shown.length) {
    update.updatedTime = firebase.firestore.FieldValue.serverTimestamp();
  }
  await db.doc(favoritePath).update(update);
  if (hideAll.length && shown.length) {
    await db
      .doc(favoritePath)
      .update({ listingIdsWithPicturesHidden: firebase.firestore.FieldValue.arrayRemove(...shown) });
  }
};

/**
 * What an add of this listing carries: the realtor's saved presentation, read through the server
 * (`lookupListingPresentationForAdd` — shared a minute, forgotten on a save), and the scan of their
 * other pages only when the server cannot be reached.
 */
export const getListingPresentationForAdd = (
  userEmail: string,
  listingId: string
): Promise<ListingPresentationEdits | null> =>
  lookupListingPresentationForAdd(userEmail, listingId, () =>
    getListingPriorEditsFromOtherMinisites(userEmail, listingId)
  );

/**
 * The reusable, listing-intrinsic presentation edits a realtor can make for a
 * listing (photo visibility + description). Deliberately excludes `listingNotes`,
 * which is contact-specific. Used to carry edits from the "customize before add"
 * dialog into one or more minisites, and to surface a realtor's prior edits.
 * The hidden set is kept whatever the mode ("show all" / "hide all" park it).
 */
export type ListingPriorEdits = ListingPresentationEdits;

const firestoreTimeToMillis = (time: unknown): number => {
  if (!time) return 0;
  if (typeof (time as { toDate?: () => Date }).toDate === "function") {
    return (time as { toDate: () => Date }).toDate().getTime();
  }
  const parsed = new Date(time as string | number | Date).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const hasListingPresentationEdits = (
  favorite: FavoriteListingsOfContact,
  listingId: string
): boolean => {
  const details = favorite.additionalDetails?.[listingId];
  const hasDescription = Boolean(details?.descriptionOverride?.trim());
  const hasAllPicturesHidden = Boolean(favorite.listingIdsWithPicturesHidden?.includes(listingId));
  const hasSomePicturesHidden = Boolean(details?.hiddenPictures?.pictures?.length);
  return hasDescription || hasAllPicturesHidden || hasSomePicturesHidden;
};

/**
 * Reads the presentation edits (photos + description) for a listing from a single
 * minisite doc. Notes are intentionally excluded (they are contact-specific).
 */
export const getListingEditsFromFavorite = (
  favorite: FavoriteListingsOfContact,
  listingId: string
): ListingPriorEdits => ({
  picturesVisibilityMode: getListingPicturesVisibility(listingId, favorite),
  hiddenPictures: favorite.additionalDetails?.[listingId]?.hiddenPictures?.pictures ?? [],
  descriptionOverride: favorite.additionalDetails?.[listingId]?.descriptionOverride ?? ""
});

/**
 * Discovers a realtor's prior presentation edits for a listing by querying the
 * realtor's OTHER minisites that already contain it (no separate state is kept).
 * Returns the edits from the most recently updated matching minisite, or null
 * when the listing was never customized anywhere. Photos + description only.
 */
export const getListingPriorEditsFromOtherMinisites = async (
  userEmail: string,
  listingId: string
): Promise<ListingPriorEdits | null> => {
  try {
    const snapshot = await getContactsThatHaveListingInFavoritesQuery(userEmail, listingId).get();

    const candidates = snapshot.docs
      .map((doc) => doc.data())
      .filter((favorite) => hasListingPresentationEdits(favorite, listingId));

    if (!candidates.length) return null;

    // Prefer the most recently updated minisite. updatedTime may be absent on
    // purely client-created docs — those sort last, with a stable fallback.
    candidates.sort(
      (a, b) => firestoreTimeToMillis(b.updatedTime) - firestoreTimeToMillis(a.updatedTime)
    );

    return getListingEditsFromFavorite(candidates[0], listingId);
  } catch (error) {
    console.error("Error fetching prior listing edits from other minisites", error);
    return null;
  }
};

/**
 * Adds a listing to a minisite AND applies presentation edits in one write — the fields exactly as the
 * server's save writes them (`minisitePresentationFields`), so the page cannot be told apart from one
 * the save reached. ⚠️ The mode and the hidden SET are independent: the set is carried whatever the
 * mode, and a mode never clears it (`enabled` decides whether it applies).
 */
export const addListingToFavoritesWithEdits = async (
  favoritePath: string,
  listingId: string,
  edits: ListingPriorEdits
) => {
  if (!favoritePath) {
    console.warn("No favorite path provided to addListingToFavoritesWithEdits");
    return;
  }

  await writeListingsToFavorites(favoritePath, [{ listingId, edits }]);
};

/**
 * Bulk per-contact entry point: ensures the contact's minisite doc exists, then adds the listing with
 * the presentation the caller already looked up (`getListingPresentationForAdd`, once for every
 * contact; null: shown as the ad is). Contacts that ALREADY have the listing are left untouched
 * (forward-only — never rewrite an existing minisite's edits).
 */
export const createFavoriteIfNotExistsAndAddListingWithEdits = async (
  userEmail: string,
  contactId: string,
  listingId: string,
  edits: ListingPriorEdits | null
): Promise<string | undefined> => {
  await createFavoriteIfNotExists(userEmail, contactId);

  const favoriteDocs = await getFavoritesListingsQuery(userEmail, contactId).get();

  if (favoriteDocs.empty) {
    console.error("Got empty favorites after creation, should not happen");
    return;
  }

  const favoriteDoc = pickMinisitePage(favoriteDocs.docs) ?? favoriteDocs.docs[0];

  const alreadyHasListing = favoriteDoc.data().listingIds?.includes(listingId);
  if (alreadyHasListing) {
    return favoriteDoc.id;
  }

  await writeListingsToFavorites(favoriteDoc.ref.path, [{ listingId, edits }]);

  return favoriteDoc.id;
};

// Awaited so a caller's try/catch actually sees a failed write (it used to resolve immediately).
export const removeListingFromFavorites = async (favoritePath: string, listingId: string) => {
  await removeListingsFromFavorites(favoritePath, [listingId]);
};

export const removeListingsFromFavorites = async (favoritePath: string, listingIds: string[]) => {
  console.log("Removing listings from favorites", listingIds);

  if (favoritePath) {
    const deletedAdditionalDetailsFields: Record<string, firebase.firestore.FieldValue> = {};

    for (const listingId of listingIds) {
      const indexAdditionalHiddenPicturesFieldKey = getStringListingHiddenPicturesIndex(listingId);

      const indexAdditionalDescriptionOverrideFieldKey =
        getStringListingDescriptionOverrideIndex(listingId);

      deletedAdditionalDetailsFields[indexAdditionalHiddenPicturesFieldKey] =
        firebase.firestore.FieldValue.delete();

      deletedAdditionalDetailsFields[indexAdditionalDescriptionOverrideFieldKey] =
        firebase.firestore.FieldValue.delete();
    }

    await db
      .doc(favoritePath)
      .withConverter(GenericConverter<FavoriteListingsOfContact>())
      .update({
        listingIds: firebase.firestore.FieldValue.arrayRemove(...listingIds),
        listingIdsWithPicturesHidden: firebase.firestore.FieldValue.arrayRemove(...listingIds),
        ...deletedAdditionalDetailsFields
      });
  }
};

// A list's listings go on and off the page as any listing does: each new one with the realtor's
// presentation, and a removed one with its presentation on this page, so re-adding it later cannot
// meet a stale "hide every photo".
export const addListingsListToFavorites = async (
  favoritePath: string,
  list: ListingsRealtorSavedList
) => {
  console.log("Adding listings list to favorites", list.id);

  await db
    .doc(favoritePath)
    .withConverter(GenericConverter<FavoriteListingsOfContact>())
    .update({
      listingsListsIds: firebase.firestore.FieldValue.arrayUnion(list.id) as any as string[]
    });
  await addListingsToFavorites(favoritePath, list.listingIds);
};

export const removeListingsListFromFavorites = async (
  favoritePath: string,
  list: ListingsRealtorSavedList
) => {
  console.log("Removing listings list from favorites", list.id);

  await db
    .doc(favoritePath)
    .withConverter(GenericConverter<FavoriteListingsOfContact>())
    .update({
      listingsListsIds: firebase.firestore.FieldValue.arrayRemove(list.id) as any as string[]
    });
  if (list.listingIds.length) await removeListingsFromFavorites(favoritePath, list.listingIds);
};

export const getRealtorListingsBlacklistQuery = (realtorId: string) => {
  return db
    .collection(`listings-realtor-blacklist`)
    .doc(realtorId)
    .withConverter(GenericConverter<ListingsRealtorBlacklist>());
};

export const addListingToRealtorBlackList = async (realtorId: string, listingId: string) => {
  console.log("About to add listing to realtor blacklist", listingId);

  const blacklistPath = db
    .collection(`listings-realtor-blacklist`)
    .doc(realtorId)
    .withConverter(GenericConverter<ListingsRealtorBlacklist>());

  await blacklistPath.set(
    {
      realtorId: realtorId,
      listingIds: firebase.firestore.FieldValue.arrayUnion(listingId) as any as string[]
    },
    { merge: true }
  );
};

export const removeListingFromRealtorBlackList = async (realtorId: string, listingId: string) => {
  console.log("About to remove listing from realtor blacklist", listingId);

  const blacklistPath = db
    .collection(`listings-realtor-blacklist`)
    .doc(realtorId)
    .withConverter(GenericConverter<ListingsRealtorBlacklist>());

  await blacklistPath.set(
    {
      realtorId: realtorId,
      listingIds: firebase.firestore.FieldValue.arrayRemove(listingId) as any as string[]
    },
    { merge: true }
  );
};
